import { white } from '@kmns-internal/furikake/tokens'
import { AddOutlined, Delete } from '@mui/icons-material'
import { TabContext, TabPanel } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Drawer,
  Stack,
  Tab,
  TablePagination,
  Tabs,
} from '@mui/material'
import { Employee, GetEmployeesRequest } from '@ulysses-inc/harami_api_client'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import { CampaignBannerWrapper } from 'src/features/campaignBanner'
import { GlobalNavigation } from 'src/features/globalNavigation/GlobalNavigation'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import employeesOperations from 'src/state/ducks/employees/operations'
import { GetEmployeesFilter } from 'src/state/ducks/employees/types'
import placesOperations from 'src/state/ducks/places/operations'
import { RootState } from 'src/state/store'
import EmployeeList from './EmployeeList'
import EmployeesFilterDropDown from './EmployeesFilterDropDown'
import EditEmployee from './editEmployees/EditEmployee'
import ImportEmployees from './importEmployees/ImportEmployees'

enum EmployeeTabIndex {
  EmployeeList = '1',
  EmployeeImport = '2',
}

const Employees = () => {
  const dispatch = useDispatch()

  const {
    isLoading,
    request,
    filter,
    segmentIndex,
    count,
    isShowEditEmployeeDrawer,
  } = useSelector((state: RootState) => state.employeesState.employees)

  const [chooseEmployees, setChooseEmployees] = useState<{
    [key: string]: boolean
  }>({})
  const { filters, setListFilter } = useListFilter()

  const changeEmployeesPage = (
    page: number,
    pageSize?: number,
    filter?: GetEmployeesFilter,
  ) => {
    employeesOperations.changeEmployeesPage(dispatch, page, pageSize, filter)
  }
  const changeEmployeesSize = (
    pageSize: number,
    filter?: GetEmployeesFilter,
  ) => {
    employeesOperations.changeEmployeesSize(dispatch, pageSize, filter)
  }
  const updatePagination = (limit: number, offset: number) => {
    employeesOperations.updatePagination(dispatch, limit, offset)
  }
  const getPlaces = () => {
    placesOperations.getPlaces(dispatch, {})
    placesOperations.getPlaceGroups(dispatch)
  }
  const updateSegmentIndex = (index: string) => {
    employeesOperations.updateSegmentIndex(dispatch, index)
  }
  const changeIsShowEditEmployeeDrawer = (isShow: boolean) => {
    employeesOperations.changeIsShowEditEmployeeDrawer(dispatch, isShow)
  }
  const openAddEmployee = () => {
    employeesOperations.updateActiveEmployeeUUID(dispatch, '')
    employeesOperations.changeIsShowEditEmployeeDrawer(dispatch, true)
  }
  // 削除ダイアログ
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const handleMenuDelete = () => {
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }
  const deleteMultipleEmployees = (
    uuids: string[],
    request: GetEmployeesRequest,
    filter?: GetEmployeesFilter,
  ) => {
    const employees: Employee[] = uuids.map(uuid => {
      return {
        uuid,
        name: '',
        employeeCode: '',
      }
    })
    employeesOperations.deleteMultipleEmployees(
      dispatch,
      employees,
      request,
      filter,
    )
  }

  useEffect(() => {
    getPlaces()
    if (filters && filters.employees) {
      const employeesFilter = filters.employees
      updatePagination(
        employeesFilter.pagination?.limit ?? 25,
        employeesFilter.pagination?.offset ?? 0,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setListFilter({
      ...(filters ?? {}),
      employees: {
        pagination: request,
        filter: filters?.employees?.filter,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.limit, request.offset])

  const currentPage =
    request.offset === undefined || request.limit === undefined
      ? 1
      : request.offset / request.limit + 1

  // 閉じるボタン or Drawer外のクリックしたとき
  const onClose = () => {
    updateSegmentIndex(EmployeeTabIndex.EmployeeList)
    changeIsShowEditEmployeeDrawer(false)
  }

  const renderEmployeeList = () => {
    return (
      <Stack>
        <Stack direction="row" justifyContent="space-between" p={2}>
          <EmployeesFilterDropDown />
          <Stack direction="row" spacing={2}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => openAddEmployee()}
            >
              従業員を追加
            </Button>
            <Button
              color="error"
              variant="outlined"
              startIcon={<Delete />}
              onClick={handleMenuDelete}
              disabled={Object.keys(chooseEmployees).length === 0}
            >
              従業員一括削除
            </Button>
          </Stack>
        </Stack>
        {!isLoading && (
          <EmployeeList
            chooseEmployees={chooseEmployees}
            setChooseEmployees={setChooseEmployees}
          />
        )}
        {isLoading && <Loading />}
      </Stack>
    )
  }

  return (
    // TODO: サイドメニュー描写の責務を`Routes.tsx`に移植する
    <GlobalNavigation
      resetPagination={() => {
        setListFilter({
          ...(filters ?? {}),
          employees: {
            pagination: { limit: 25, offset: 0 },
            filter: filters?.employees?.filter,
          },
        })
      }}
    >
      <CampaignBannerWrapper>
        <Stack>
          <Header>
            <HeaderTitle title="従業員" />
          </Header>
          <TabContext value={segmentIndex}>
            <Tabs
              value={segmentIndex}
              sx={{ backgroundColor: white }}
              onChange={(_, value) => updateSegmentIndex(value)}
            >
              <Tab
                key={EmployeeTabIndex.EmployeeList}
                label="従業員一覧"
                value={EmployeeTabIndex.EmployeeList}
              ></Tab>
              <Tab
                key={EmployeeTabIndex.EmployeeImport}
                label="従業員インポート"
                value={EmployeeTabIndex.EmployeeImport}
              ></Tab>
            </Tabs>
            <TabPanel value={EmployeeTabIndex.EmployeeList} sx={{ p: 0 }}>
              {renderEmployeeList()}
              <Drawer
                anchor="right"
                open={isShowEditEmployeeDrawer}
                onClose={() => onClose()}
                slotProps={{ paper: { sx: { width: 600, padding: 0 } } }}
                ModalProps={{ keepMounted: false }}
              >
                <EditEmployee />
              </Drawer>
              <TablePagination
                component="div"
                count={count}
                page={currentPage - 1}
                // API 側の Pagination は 1 オリジン
                // MUI の TablePagination は 0 オリジン
                onPageChange={(_, page) =>
                  changeEmployeesPage(page + 1, request.limit ?? 25, filter)
                }
                rowsPerPage={request.limit ?? 25}
                onRowsPerPageChange={event => {
                  const size = parseInt(event.target.value, 10)
                  changeEmployeesSize(size, filter)
                }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="1ページあたり:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}〜${to} (${count !== -1 ? count : `不明`} 件中)`
                }
                sx={{
                  '& .MuiTablePagination-selectLabel': {
                    margin: 0,
                  },
                  '& .MuiTablePagination-displayedRows': {
                    margin: 0,
                  },
                  bgcolor: white,
                }}
              />
            </TabPanel>
            <TabPanel value={EmployeeTabIndex.EmployeeImport} sx={{ p: 0 }}>
              <ImportEmployees />
            </TabPanel>
          </TabContext>
        </Stack>
        <Dialog open={openDeleteDialog}>
          <DialogTitle>
            選択した従業員を一括で削除しますがよろしいですか？
          </DialogTitle>
          <DialogActions>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleCloseDeleteDialog}
            >
              キャンセル
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                deleteMultipleEmployees(
                  Object.keys(chooseEmployees),
                  request,
                  filter,
                )
                setChooseEmployees({})
                handleCloseDeleteDialog()
              }}
            >
              削除
            </Button>
          </DialogActions>
        </Dialog>
      </CampaignBannerWrapper>
    </GlobalNavigation>
  )
}

export default Employees
